import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import cn from 'classnames';
import moment from 'moment';

import { Button, Input, Textarea, Loader, Logo, Dropdown } from 'src/components';
import {
  getShare,
  accessShareWithPassword,
  addPublicComment,
  approveAsset,
  bulkApproveAssets,
  trackShareDownload,
  PublicCommentRequest,
  PublicApprovalRequest,
  BulkApprovalRequest,
} from 'src/api/functions/shareApi';
import {
  Share,
  ShareResponse,
  ShareAsset,
  ShareAssetConversion,
  getShareTitle,
  getShareDescription,
  getShareExpiry,
} from 'src/types/TShare';
import { getODIThumbnailUrl, getODIDownloadUrl, buildWatermarkParam, WatermarkConfig } from 'src/utils/previews';
import {
  Key,
  WarningCircle,
  DownloadSimple,
  Checks,
  X,
  Eye,
  CaretDown,
  CaretLeft,
  CaretRight,
} from 'src/common/icons';

import styles from './PublicShare.module.scss';

// Social media image size presets for ODI downloads
const SOCIAL_MEDIA_PRESETS = [
  { key: 'instagram-square', label: 'Instagram Square', width: 1080, height: 1080 },
  { key: 'instagram-portrait', label: 'Instagram Portrait', width: 1080, height: 1350 },
  { key: 'facebook', label: 'Facebook / LinkedIn', width: 1200, height: 630 },
  { key: 'twitter', label: 'Twitter / X', width: 1200, height: 675 },
  { key: 'youtube', label: 'YouTube Thumbnail', width: 1280, height: 720 },
  { key: 'pinterest', label: 'Pinterest', width: 1000, height: 1500 },
  { key: 'hd-landscape', label: 'HD Landscape', width: 1920, height: 1080 },
];

type PublicShareParams = {
  id: string;
};

type ShareState = 'loading' | 'ready' | 'password_required' | 'expired' | 'not_found' | 'error';

export const PublicShare: React.FC = () => {
  const { id } = useParams<PublicShareParams>();

  // State
  const [shareState, setShareState] = useState<ShareState>('loading');
  const [share, setShare] = useState<Share | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Password state
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);

  // Comment form state
  const [commentName, setCommentName] = useState('');
  const [commentEmail, setCommentEmail] = useState('');
  const [commentText, setCommentText] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [commentSuccess, setCommentSuccess] = useState(false);

  // Selected asset for lightbox/approval (tracked by index so we can navigate prev/next)
  const [selectedAssetIndex, setSelectedAssetIndex] = useState<number | null>(null);
  
  // Shared reviewer info (persists across all review forms)
  const [reviewerName, setReviewerName] = useState('');
  const [reviewerEmail, setReviewerEmail] = useState('');
  
  // Individual asset approval state
  const [approvalNotes, setApprovalNotes] = useState('');
  const [isSubmittingApproval, setIsSubmittingApproval] = useState(false);

  // Per-asset approval status tracking (local state, replaces browser alerts)
  const [assetStatuses, setAssetStatuses] = useState<Record<string, 'APPROVED' | 'REJECTED'>>({});

  // Bulk approval state
  const [bulkApprovalNotes, setBulkApprovalNotes] = useState('');
  const [isSubmittingBulkApproval, setIsSubmittingBulkApproval] = useState(false);
  const [bulkApprovalSuccess, setBulkApprovalSuccess] = useState<string>('');

  // Custom size download state
  const [customSizeAsset, setCustomSizeAsset] = useState<ShareAsset | null>(null);
  const [customWidth, setCustomWidth] = useState('1080');
  const [customHeight, setCustomHeight] = useState('1080');
  const [customFormat, setCustomFormat] = useState('jpg');
  const [customSizeWatermark, setCustomSizeWatermark] = useState(false);

  // Social media sizes modal state
  const [socialSizesAsset, setSocialSizesAsset] = useState<ShareAsset | null>(null);
  const [socialSizesWatermarked, setSocialSizesWatermarked] = useState(false);
  const [socialSizesFormat, setSocialSizesFormat] = useState('jpg');

  // Derived values
  const title = share ? getShareTitle(share) : '';
  const description = share ? getShareDescription(share) : '';
  const expiry = share ? getShareExpiry(share) : undefined;
  const assets = share?.assets || [];
  const allowFeedback = share?.allow_feedback || false;
  const allowApproval = share?.allow_approval || false;
  const branding = share?.branding;
  
  // Check if ODI (Custom Size Downloads) is enabled
  const odiEnabled = share?.download_options?.image?.odi?.enabled ?? false;
  const odiFormats = share?.download_options?.image?.odi?.formats || ['jpg', 'png'];
  
  // Check if branded ODI (Branded Custom Size Downloads) is enabled
  const odiWmEnabled = share?.download_options?.image?.odi_wm?.enabled ?? false;
  const odiWmFormats = share?.download_options?.image?.odi_wm?.formats || ['jpg', 'png'];

  // Preview options: controls whether thumbnails/previews show watermarked images
  const previewOdiWmEnabled = share?.preview_options?.image?.odi_wm?.enabled ?? true;

  // Build watermark config from share branding (used for both preview and download watermarks)
  const watermarkConfig: WatermarkConfig | null = useMemo(() => {
    if (!branding?.logo_url) return null;
    return {
      imageUrl: branding.logo_url,
      placement: share?.download_options?.image?.odi_wm?.placement || 'center',
      scale: share?.download_options?.image?.odi_wm?.scale ?? 20,
      opacity: share?.download_options?.image?.odi_wm?.opacity ?? 40,
    };
  }, [branding?.logo_url, share?.download_options?.image?.odi_wm]);

  // Apply branding colors to CSS variables
  const brandingStyle = useMemo(() => {
    if (!branding) return {};
    return {
      '--share-primary-color': branding.primary_color || '#4255FF',
      '--share-bg-color': branding.background_color || '#ffffff',
    } as React.CSSProperties;
  }, [branding]);

  // Fetch share on mount
  useEffect(() => {
    const fetchShare = async () => {
      try {
        const result = await getShare(id);
        
        if ('error' in result) {
          const errorStatus = (result as any).status;
          if (errorStatus === 403) {
            setShareState('password_required');
          } else if (errorStatus === 410) {
            setShareState('expired');
          } else if (errorStatus === 404) {
            setShareState('not_found');
          } else {
            setShareState('error');
            setErrorMessage(result.message || 'Failed to load share');
          }
          return;
        }

        const shareData = (result as ShareResponse).data;
        setShare(shareData);
        setShareState('ready');

        // Initialize asset statuses from API approval_status
        if (shareData.assets) {
          const initialStatuses: Record<string, 'APPROVED' | 'REJECTED'> = {};
          shareData.assets.forEach(a => {
            if (a.approval_status === 'APPROVED' || a.approval_status === 'REJECTED') {
              initialStatuses[a.id] = a.approval_status;
            }
          });
          if (Object.keys(initialStatuses).length > 0) {
            setAssetStatuses(initialStatuses);
          }
        }
      } catch (err) {
        setShareState('error');
        setErrorMessage('An unexpected error occurred');
      }
    };

    fetchShare();
  }, [id]);

  // Handle password submission
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setIsSubmittingPassword(true);

    try {
      const result = await accessShareWithPassword(id, password);

      if ('error' in result) {
        setPasswordError('Incorrect password. Please try again.');
        setIsSubmittingPassword(false);
        return;
      }

      const shareData = (result as ShareResponse).data;
      setShare(shareData);
      setShareState('ready');

      // Initialize asset statuses from API approval_status
      if (shareData.assets) {
        const initialStatuses: Record<string, 'APPROVED' | 'REJECTED'> = {};
        shareData.assets.forEach(a => {
          if (a.approval_status === 'APPROVED' || a.approval_status === 'REJECTED') {
            initialStatuses[a.id] = a.approval_status;
          }
        });
        if (Object.keys(initialStatuses).length > 0) {
          setAssetStatuses(initialStatuses);
        }
      }
    } catch (err) {
      setPasswordError('An error occurred. Please try again.');
    } finally {
      setIsSubmittingPassword(false);
    }
  };

  // Handle comment submission
  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSubmittingComment(true);
    setCommentSuccess(false);

    try {
      const data: PublicCommentRequest = {
        comment: commentText.trim(),
        commenter_name: commentName.trim() || undefined,
        commenter_email: commentEmail.trim() || undefined,
      };

      const result = await addPublicComment(id, data);

      if ('error' in result) {
        alert('Failed to submit comment. Please try again.');
        return;
      }

      setCommentText('');
      setCommentSuccess(true);
      setTimeout(() => setCommentSuccess(false), 3000);
    } catch (err) {
      alert('An error occurred. Please try again.');
    } finally {
      setIsSubmittingComment(false);
    }
  };

  // Handle approval submission
  const handleApprovalSubmit = async (status: 'APPROVED' | 'REJECTED') => {
    if (!selectedAsset) return;

    setIsSubmittingApproval(true);

    try {
      const data: PublicApprovalRequest = {
        status,
        reviewer_name: reviewerName.trim() || undefined,
        reviewer_email: reviewerEmail.trim() || undefined,
        notes: approvalNotes.trim() || undefined,
      };

      const result = await approveAsset(id, selectedAsset.id, data);

      if ('error' in result) {
        alert('Failed to submit approval. Please try again.');
        return;
      }

      // Update local status tracking and close modal
      setAssetStatuses(prev => ({ ...prev, [selectedAsset.id]: status }));
      setSelectedAssetIndex(null);
      setApprovalNotes('');
    } catch (err) {
      alert('An error occurred. Please try again.');
    } finally {
      setIsSubmittingApproval(false);
    }
  };

  // Handle bulk approval submission
  const handleBulkApprovalSubmit = async (status: 'APPROVED' | 'REJECTED') => {
    if (assets.length === 0) return;

    setIsSubmittingBulkApproval(true);
    setBulkApprovalSuccess('');

    try {
      const data: BulkApprovalRequest = {
        assets: assets.map(asset => ({
          asset_id: asset.id,
          status,
          ...(bulkApprovalNotes.trim() && { notes: bulkApprovalNotes.trim() }),
        })),
        reviewer_name: reviewerName.trim() || '',
        reviewer_email: reviewerEmail.trim() || '',
      };

      const result = await bulkApproveAssets(id, data);

      if ('error' in result) {
        alert('Failed to submit bulk approval. Please try again.');
        return;
      }

      // Mark all assets with the status
      const allStatuses: Record<string, 'APPROVED' | 'REJECTED'> = {};
      assets.forEach(asset => { allStatuses[asset.id] = status; });
      setAssetStatuses(prev => ({ ...prev, ...allStatuses }));

      setBulkApprovalSuccess(`All ${assets.length} assets have been ${status.toLowerCase()}!`);
    } catch (err) {
      alert('An error occurred. Please try again.');
    } finally {
      setIsSubmittingBulkApproval(false);
    }
  };

  // Handle download - open download URL in new tab and track analytics
  const handleDownload = (downloadUrl: string, assetId: string, downloadKey: string) => {
    // Track download event (fire and forget)
    trackShareDownload(id, { asset_id: assetId, download_key: downloadKey }).catch(() => {
      // Silently ignore tracking errors
    });
    window.open(downloadUrl, '_blank');
  };

  // Get thumbnail URL using ODI from shared_links
  // Apply watermark when preview_options.image.odi_wm is enabled
  const getAssetThumbnailUrl = (asset: ShareAsset, width = 300, height = 300): string => {
    if (asset.shared_links?.odi) {
      const useWatermark = previewOdiWmEnabled && watermarkConfig;
      return getODIThumbnailUrl({
        odi: asset.shared_links.odi,
        width,
        height,
        watermark: useWatermark ? watermarkConfig! : undefined,
      });
    }
    return '';
  };

  // Check if asset is an image
  const isImageAsset = (asset: ShareAsset): boolean => {
    return asset.content_type?.startsWith('image/') ?? false;
  };

  // Check if asset is a video
  const isVideoAsset = (asset: ShareAsset): boolean => {
    return asset.content_type?.startsWith('video/') ?? false;
  };

  // Check if browser supports HLS playback
  const supportsHLS = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const video = document.createElement('video');
    // Check for native HLS support (Safari) or MediaSource API (for hls.js fallback)
    return video.canPlayType('application/vnd.apple.mpegurl') !== '' || 
           typeof window.MediaSource !== 'undefined';
  }, []);

  // Get preferred view conversion (prefer HLS for videos if supported)
  const getPreferredViewConversion = (conversions: ShareAssetConversion[]): ShareAssetConversion | undefined => {
    if (supportsHLS) {
      const hlsConversion = conversions.find(c => c.key === 'hls-video' && c.display_url);
      if (hlsConversion) return hlsConversion;
    }
    return conversions[0];
  };

  // Filter conversions for download (exclude HLS as it's not a downloadable format)
  const getDownloadableConversions = (conversions: ShareAssetConversion[]): ShareAssetConversion[] => {
    return conversions.filter(c => c.key !== 'hls-video');
  };

  // Map for video download labels to include "Video" in the label
  const VIDEO_LABEL_MAP: Record<string, string> = {
    '480p-video': 'SD Video (480p)',
    '720p-video': 'HD Video (720p)',
    '1080p-video': 'Full HD Video (1080p)',
    '480p-wm-video': 'SD Video Watermarked',
    '720p-wm-video': 'HD Video Watermarked',
    '1080p-wm-video': 'Full HD Video Watermarked',
  };

  // Get display label for a conversion (uses mapped label for videos, falls back to API label)
  const getConversionLabel = (conv: ShareAssetConversion): string => {
    return VIDEO_LABEL_MAP[conv.key] || conv.label;
  };

  // Check if asset can be previewed
  const canPreviewAsset = (asset: ShareAsset, viewConversions: ShareAssetConversion[]): boolean => {
    // Assets with ODI can always be previewed (ODI generates image from any asset type)
    if (asset.shared_links?.odi) {
      return true;
    }
    // Assets without ODI need a view conversion with display_url
    const hasViewUrl = viewConversions.some(conv => conv.display_url);
    return hasViewUrl;
  };

  // Currently selected asset for the preview/approval modal, derived from index
  const selectedAsset: ShareAsset | null =
    selectedAssetIndex !== null ? assets[selectedAssetIndex] ?? null : null;

  // Derive the preview URL for the selected asset (videos prefer view conversion,
  // images and fallback use the ODI endpoint). Recomputes automatically when
  // navigating prev/next so the modal swaps in the new asset's media.
  const selectedDisplayUrl: string = useMemo(() => {
    if (!selectedAsset) return '';

    const conversions = selectedAsset.shared_links?.conversions || [];
    const viewConversions = conversions.filter(c => c.key !== 'original');

    if (isVideoAsset(selectedAsset)) {
      const preferred = getPreferredViewConversion(viewConversions);
      if (preferred?.display_url) return preferred.display_url;
    }

    if (selectedAsset.shared_links?.odi) {
      const useWatermark = previewOdiWmEnabled && watermarkConfig;
      return getODIThumbnailUrl({
        odi: selectedAsset.shared_links.odi,
        width: 1920,
        height: 1080,
        watermark: useWatermark ? watermarkConfig! : undefined,
      });
    }

    const firstView = viewConversions.find(c => c.display_url);
    return firstView?.display_url || '';
  }, [selectedAsset, supportsHLS, watermarkConfig, previewOdiWmEnabled]);

  // Close the preview/approval modal and reset per-asset form state
  const closeModal = useCallback(() => {
    setSelectedAssetIndex(null);
    setApprovalNotes('');
  }, []);

  // Navigate to the previous asset in the modal (wraps from first to last)
  const goToPrev = useCallback(() => {
    if (selectedAssetIndex === null || assets.length === 0) return;
    setSelectedAssetIndex((selectedAssetIndex - 1 + assets.length) % assets.length);
    setApprovalNotes('');
  }, [selectedAssetIndex, assets.length]);

  // Navigate to the next asset in the modal (wraps from last to first)
  const goToNext = useCallback(() => {
    if (selectedAssetIndex === null || assets.length === 0) return;
    setSelectedAssetIndex((selectedAssetIndex + 1) % assets.length);
    setApprovalNotes('');
  }, [selectedAssetIndex, assets.length]);

  // Keyboard shortcuts while the preview/approval modal is open
  useEffect(() => {
    if (selectedAssetIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNext();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        closeModal();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedAssetIndex, goToPrev, goToNext, closeModal]);

  // Handle ODI preset download (used by Social Media Sizes modal)
  const handleODIPresetDownload = (asset: ShareAsset, presetKey: string, format: string, watermark?: WatermarkConfig) => {
    const preset = SOCIAL_MEDIA_PRESETS.find(p => p.key === presetKey);
    if (!preset || !asset.shared_links?.odi) return;

    const downloadUrl = getODIDownloadUrl({
      odi: asset.shared_links.odi,
      width: preset.width,
      height: preset.height,
      format,
      filename: asset.name?.replace(/\.[^/.]+$/, '') || 'download',
      watermark,
    });
    
    handleDownload(downloadUrl, asset.id, `odi-${watermark ? 'wm-' : ''}${presetKey}`);
  };

  // Handle custom size download
  const handleCustomSizeDownload = () => {
    if (!customSizeAsset?.shared_links?.odi) return;

    const width = Math.min(2048, Math.max(1, parseInt(customWidth) || 1080));
    const height = Math.min(1536, Math.max(1, parseInt(customHeight) || 1080));

    const downloadUrl = getODIDownloadUrl({
      odi: customSizeAsset.shared_links.odi,
      width,
      height,
      format: customFormat,
      filename: customSizeAsset.name?.replace(/\.[^/.]+$/, '') || 'download',
      watermark: customSizeWatermark && watermarkConfig ? watermarkConfig : undefined,
    });
    
    handleDownload(downloadUrl, customSizeAsset.id, `odi-${customSizeWatermark ? 'wm-' : ''}custom-${width}x${height}`);
    setCustomSizeAsset(null);
  };

  const formatExpiryDate = (): string => {
    // Use expiry_utc directly from share (ISO 8601 format)
    if (share?.expiry_utc) {
      return `Expires ${moment.utc(share.expiry_utc).local().format('MMMM D, YYYY')}`;
    }
    // Fallback to computed expiry (Unix timestamp)
    if (expiry) {
    return `Expires ${moment.unix(expiry).format('MMMM D, YYYY')}`;
    }
    return 'Does not expire';
  };

  // Loading state
  if (shareState === 'loading') {
    return (
      <div className={styles.page}>
        <div className={styles.loadingWrapper}>
          <Loader />
          <p>Loading share...</p>
        </div>
      </div>
    );
  }

  // Password required state
  if (shareState === 'password_required') {
    return (
      <div className={styles.page}>
        <div className={styles.centerCard}>
          <div className={styles.cardIcon}>
            <Key />
          </div>
          <h2>Password Protected</h2>
          <p>This share requires a password to access.</p>

          <form onSubmit={handlePasswordSubmit} className={styles.passwordForm}>
            <Input
              type="password"
              label="Password"
              value={password}
              onChange={setPassword}
              placeholder="Enter password"
            />
            {passwordError && (
              <span className={styles.errorText}>{passwordError}</span>
            )}
            <Button
              type="submit"
              mode="primary"
              loading={isSubmittingPassword}
              disabled={!password}
            >
              Access Share
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Expired state
  if (shareState === 'expired') {
    return (
      <div className={styles.page}>
        <div className={styles.centerCard}>
          <div className={cn(styles.cardIcon, styles.warning)}>
            <WarningCircle />
          </div>
          <h2>Share Expired</h2>
          <p>This share has expired and is no longer available.</p>
        </div>
      </div>
    );
  }

  // Not found state
  if (shareState === 'not_found') {
    return (
      <div className={styles.page}>
        <div className={styles.centerCard}>
          <div className={cn(styles.cardIcon, styles.warning)}>
            <WarningCircle />
          </div>
          <h2>Share Not Found</h2>
          <p>The share you're looking for doesn't exist or has been deleted.</p>
        </div>
      </div>
    );
  }

  // Error state
  if (shareState === 'error') {
    return (
      <div className={styles.page}>
        <div className={styles.centerCard}>
          <div className={cn(styles.cardIcon, styles.warning)}>
            <WarningCircle />
          </div>
          <h2>Error</h2>
          <p>{errorMessage}</p>
        </div>
      </div>
    );
  }

  // Ready state - show the share
  return (
    <div className={styles.page} style={brandingStyle}>
      {/* Header */}
      <header className={styles.header}>
        {branding?.logo_url ? (
          <img 
            src={branding.logo_url} 
            alt="Logo" 
            className={styles.brandingLogo}
          />
        ) : (
          <Logo />
        )}
        <span className={styles.headerDivider}>|</span>
        <span className={styles.headerText}>Shared with you</span>
      </header>

      {/* Share Info */}
      <div className={styles.shareInfo}>
        <h1 className={styles.title}>{title}</h1>
        {description && <p className={styles.description}>{description}</p>}
        <div className={styles.meta}>
          <span className={styles.assetCount}>
            {assets.length} asset{assets.length !== 1 ? 's' : ''}
          </span>
          <span className={styles.expiry}>{formatExpiryDate()}</span>
        </div>
      </div>

      {/* Assets Gallery */}
      <div className={styles.gallery}>
        {assets.length === 0 ? (
          <div className={styles.emptyGallery}>
            <p>No assets in this share.</p>
          </div>
        ) : (
          <div className={styles.assetsGrid}>
            {assets.map((asset: ShareAsset, assetIndex: number) => {
              const conversions = asset.shared_links?.conversions || [];
              // Filter out "Original" from view options
              const viewConversions = conversions.filter((c: ShareAssetConversion) => c.key !== 'original');
              // Get downloadable conversions (exclude HLS which is streaming-only)
              const downloadConversions = getDownloadableConversions(conversions);
              const hasMultipleViewOptions = viewConversions.length > 1;
              const hasMultipleDownloads = downloadConversions.length > 1;
              const hasPreview = canPreviewAsset(asset, viewConversions);

              return (
              <div key={asset.id} className={styles.assetCard}>
                <div 
                  className={cn(styles.assetThumbnail, { [styles.clickable]: hasPreview })}
                    onClick={() => {
                      if (!hasPreview) return;
                      setSelectedAssetIndex(assetIndex);
                    }}
                >
                  <img
                    src={getAssetThumbnailUrl(asset, 300, 300)}
                    alt={asset.name || 'Asset'}
                    loading="lazy"
                    decoding="async"
                  />
                  {hasPreview && (
                  <div className={styles.thumbnailOverlay}>
                    <Eye className={styles.viewIcon} />
                  </div>
                  )}
                </div>
                <div className={styles.assetInfo}>
                  <span className={styles.assetName} title={asset.name}>
                    {asset.name || 'Untitled'}
                  </span>
                  <div className={styles.assetActions}>
                      {/* Download Dropdown */}
                      {(() => {
                        const hasODI = !!asset.shared_links?.odi;
                        // ODI produces an image output, so social/custom size
                        // downloads only make sense for image and video sources.
                        // Hide these options for PDFs, docs, and other asset types.
                        const supportsODIDownload = isImageAsset(asset) || isVideoAsset(asset);
                        const odiDownloadOptions: { key: string; value: string; icon: JSX.Element }[] = [];

                        if (hasODI && supportsODIDownload && (odiEnabled || odiWmEnabled)) {
                          odiDownloadOptions.push({
                            key: 'social-sizes',
                            value: 'Social media sizes',
                            icon: <DownloadSimple />,
                          });
                          odiDownloadOptions.push({
                            key: 'custom-size',
                            value: 'Custom sizes...',
                            icon: <DownloadSimple />,
                          });
                        }

                        // Combine ODI options with conversion download options
                        const allOptions = [
                          ...odiDownloadOptions,
                          ...downloadConversions.map((conv: ShareAssetConversion) => ({
                            key: conv.key,
                            value: getConversionLabel(conv),
                            icon: <DownloadSimple />,
                          })),
                        ];

                        return allOptions.length > 0 ? (
                          <Dropdown
                            className={styles.actionDropdown}
                            variant="menu"
                            icon={allOptions.length > 1 ? <CaretDown /> : undefined}
                            options={allOptions}
                            onClick={(key) => {
                              if (key === 'social-sizes') {
                                setSocialSizesAsset(asset);
                                // Default watermark on if only branded ODI is enabled
                                setSocialSizesWatermarked(!odiEnabled && odiWmEnabled);
                                setSocialSizesFormat((odiEnabled ? odiFormats : odiWmFormats)[0] || 'jpg');
                              } else if (key === 'custom-size') {
                                setCustomSizeAsset(asset);
                                setCustomFormat(odiFormats[0] || odiWmFormats[0] || 'jpg');
                                // If only branded is enabled, default watermark on
                                setCustomSizeWatermark(!odiEnabled && odiWmEnabled);
                              } else {
                                const conv = downloadConversions.find((c: ShareAssetConversion) => c.key === key);
                                if (conv) {
                                  handleDownload(conv.download_url, asset.id, conv.key);
                                }
                              }
                            }}
                          >
                            <DownloadSimple />
                            <span>Download</span>
                          </Dropdown>
                        ) : null;
                      })()}

                    {allowApproval && (
                      <div className={styles.approvalButtons}>
                        <Button
                          mode="tertiary"
                          size="small"
                          icon={<Checks />}
                          onClick={() => setSelectedAssetIndex(assetIndex)}
                          className={cn(
                            styles.approveBtn,
                            assetStatuses[asset.id] === 'APPROVED' && styles.approvedBtn,
                            assetStatuses[asset.id] === 'REJECTED' && styles.rejectedBtn,
                          )}
                        >
                          {assetStatuses[asset.id]
                            ? `Review · ${assetStatuses[asset.id] === 'APPROVED' ? 'Approved' : 'Rejected'}`
                            : 'Review'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Comment & Bulk Approval Section */}
      {(allowFeedback || allowApproval) && (
        <div className={styles.bottomSectionsRow}>
      {/* Comment Section */}
      {allowFeedback && (
        <div className={styles.commentSection}>
          <h3>Leave a Comment</h3>
          <form onSubmit={handleCommentSubmit} className={styles.commentForm}>
            <div className={styles.commentFormRow}>
              <Input
                    label="Name (optional)"
                value={commentName}
                onChange={setCommentName}
                placeholder="Your name"
              />
              <Input
                    label="Email (optional)"
                value={commentEmail}
                onChange={setCommentEmail}
                placeholder="your@email.com"
                type="email"
              />
            </div>
            <Textarea
                  label="Comment (optional)"
              value={commentText}
              onChange={setCommentText}
              placeholder="Write your comment here..."
              rows={4}
            />
            <div className={styles.commentFormActions}>
              <Button
                type="submit"
                mode="primary"
                loading={isSubmittingComment}
              >
                Submit Comment
              </Button>
              {commentSuccess && (
                <span className={styles.successText}>
                  Comment submitted successfully!
                </span>
              )}
            </div>
          </form>
            </div>
          )}

          {/* Bulk Approval Section */}
          {allowApproval && (
            <div className={styles.bulkApprovalSection}>
              <h3>Approve or Reject All Assets</h3>
              <p className={styles.bulkApprovalDescription}>
                Quickly approve or reject all {assets.length} assets in this share at once.
              </p>
              <div className={styles.bulkApprovalForm}>
                <div className={styles.bulkApprovalFormRow}>
                  <Input
                    label="Name (optional)"
                    value={reviewerName}
                    onChange={setReviewerName}
                    placeholder="Your name"
                  />
                  <Input
                    label="Email (optional)"
                    value={reviewerEmail}
                    onChange={setReviewerEmail}
                    placeholder="your@email.com"
                    type="email"
                  />
                </div>
                <Textarea
                  label="Comment (optional)"
                  value={bulkApprovalNotes}
                  onChange={setBulkApprovalNotes}
                  placeholder="Add any notes about your approval or rejection..."
                  rows={3}
                />
                <div className={styles.bulkApprovalActions}>
                  <Button
                    mode="primary"
                    icon={<Checks />}
                    onClick={() => handleBulkApprovalSubmit('APPROVED')}
                    loading={isSubmittingBulkApproval}
                  >
                    Approve All ({assets.length})
                  </Button>
                  <Button
                    mode="tertiary"
                    icon={<X />}
                    onClick={() => handleBulkApprovalSubmit('REJECTED')}
                    loading={isSubmittingBulkApproval}
                    className={styles.rejectBtn}
                  >
                    Reject All ({assets.length})
                  </Button>
                </div>
                {bulkApprovalSuccess && (
                  <span className={styles.successText}>
                    {bulkApprovalSuccess}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Asset Preview/Approval Modal */}
      {selectedAsset && selectedAssetIndex !== null && (
        <div className={styles.modal} onClick={closeModal}>
          <div className={styles.modalWrapper} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>{selectedAsset.name || 'Untitled'}</h3>
              {assets.length > 1 && (
                <div className={styles.modalNav}>
                  <button
                    type="button"
                    className={styles.modalNavButton}
                    onClick={goToPrev}
                    aria-label="Previous asset"
                  >
                    <CaretLeft />
                  </button>
                  <button
                    type="button"
                    className={styles.modalNavButton}
                    onClick={goToNext}
                    aria-label="Next asset"
                  >
                    <CaretRight />
                  </button>
                  <span className={styles.modalCounter}>
                    {selectedAssetIndex + 1} of {assets.length}
                  </span>
                </div>
              )}
            <button
              className={styles.modalClose}
                onClick={closeModal}
                aria-label="Close"
            >
              <X />
            </button>
            </div>

            <div className={styles.modalBody}>
            <div className={styles.modalPreview}>
                {/* Use display URL if available, otherwise fall back to ODI thumbnail */}
                {selectedAsset.content_type.startsWith('video/') && selectedDisplayUrl ? (
                  <video
                    key={selectedAsset.id}
                    src={selectedDisplayUrl}
                    controls
                    autoPlay
                    className={styles.modalVideo}
                  />
                ) : (
                  <img
                    key={selectedAsset.id}
                    src={selectedDisplayUrl || getAssetThumbnailUrl(selectedAsset, 1920, 1080)}
                alt={selectedAsset.name || 'Asset'}
              />
                )}
            </div>

              {allowApproval && (
                <div className={styles.approvalForm}>
                  <h4>Review this asset</h4>
                  <Input
                    label="Name (optional)"
                    value={reviewerName}
                    onChange={setReviewerName}
                    placeholder="Your name"
                  />
                  <Input
                    label="Email (optional)"
                    value={reviewerEmail}
                    onChange={setReviewerEmail}
                    placeholder="your@email.com"
                    type="email"
                  />
                  <Textarea
                    label="Notes (optional)"
                    value={approvalNotes}
                    onChange={setApprovalNotes}
                    placeholder="Add any notes..."
                    rows={3}
                  />
                  <div className={styles.approvalActions}>
                    <Button
                      mode="primary"
                      icon={<Checks />}
                      onClick={() => handleApprovalSubmit('APPROVED')}
                      loading={isSubmittingApproval}
                    >
                      Approve
                    </Button>
                    <Button
                      mode="tertiary"
                      icon={<X />}
                      onClick={() => handleApprovalSubmit('REJECTED')}
                      loading={isSubmittingApproval}
                      className={styles.rejectBtn}
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Social Media Sizes Modal */}
      {socialSizesAsset && (
        <div className={styles.modal} onClick={() => setSocialSizesAsset(null)}>
          <div className={styles.socialSizesModal} onClick={(e) => e.stopPropagation()}>
            <button
              className={styles.modalClose}
              onClick={() => setSocialSizesAsset(null)}
            >
              <X />
            </button>

            <h3>Social Media Sizes</h3>
            <p className={styles.customSizeHint}>
              Download images optimized for social media platforms.
            </p>

            <div className={styles.formatSelector}>
              <label>Format</label>
              <select
                value={socialSizesFormat}
                onChange={(e) => setSocialSizesFormat(e.target.value)}
                className={styles.customSizeSelect}
              >
                {(socialSizesWatermarked ? odiWmFormats : odiFormats).map((fmt) => (
                  <option key={fmt} value={fmt}>
                    {fmt.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>

            {/* Watermark toggle */}
            {odiWmEnabled && (
              <label className={styles.watermarkToggle}>
                <input
                  type="checkbox"
                  checked={socialSizesWatermarked || (!odiEnabled && odiWmEnabled)}
                  onChange={(e) => setSocialSizesWatermarked(e.target.checked)}
                  disabled={!odiEnabled}
                />
                <span>Watermark</span>
              </label>
            )}

            <div className={styles.socialPresetGrid}>
              {SOCIAL_MEDIA_PRESETS.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  className={styles.socialPresetButton}
                  onClick={() => {
                    if (socialSizesAsset) {
                      handleODIPresetDownload(
                        socialSizesAsset,
                        preset.key,
                        socialSizesFormat,
                        socialSizesWatermarked && watermarkConfig ? watermarkConfig : undefined,
                      );
                    }
                  }}
                >
                  <span className={styles.presetName}>{preset.label}</span>
                  <span className={styles.presetDimensions}>{preset.width} × {preset.height}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Custom Size Download Modal */}
      {customSizeAsset && (
        <div className={styles.modal} onClick={() => setCustomSizeAsset(null)}>
          <div className={styles.customSizeModal} onClick={(e) => e.stopPropagation()}>
            <button
              className={styles.modalClose}
              onClick={() => setCustomSizeAsset(null)}
            >
              <X />
            </button>

            <h3>Custom Download Size</h3>
            <p className={styles.customSizeHint}>
              Enter custom dimensions (max 2048×1536). Image will be cropped with face-aware alignment.
            </p>

            <div className={styles.customSizeForm}>
              <div className={styles.customSizeInputs}>
                <div className={styles.customSizeField}>
                  <label>Width (px)</label>
                  <input
                    type="number"
                    min="1"
                    max="2048"
                    value={customWidth}
                    onChange={(e) => setCustomWidth(e.target.value)}
                    className={styles.customSizeInput}
                  />
                </div>
                <span className={styles.customSizeSeparator}>×</span>
                <div className={styles.customSizeField}>
                  <label>Height (px)</label>
                  <input
                    type="number"
                    min="1"
                    max="1536"
                    value={customHeight}
                    onChange={(e) => setCustomHeight(e.target.value)}
                    className={styles.customSizeInput}
                  />
                </div>
              </div>

              <div className={styles.customSizeField}>
                <label>Format</label>
                <select
                  value={customFormat}
                  onChange={(e) => setCustomFormat(e.target.value)}
                  className={styles.customSizeSelect}
                >
                  {(odiEnabled ? odiFormats : odiWmFormats).map((fmt) => (
                    <option key={fmt} value={fmt}>
                      {fmt.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>

              {/* Watermark toggle */}
              {odiWmEnabled && (
                <label className={styles.watermarkToggle}>
                  <input
                    type="checkbox"
                    checked={customSizeWatermark || (!odiEnabled && odiWmEnabled)}
                    onChange={(e) => setCustomSizeWatermark(e.target.checked)}
                    disabled={!odiEnabled}
                  />
                  <span>Watermark</span>
                </label>
              )}

              <div className={styles.customSizePresets}>
                <span className={styles.presetsLabel}>Quick presets:</span>
                <div className={styles.presetButtons}>
                  {SOCIAL_MEDIA_PRESETS.slice(0, 4).map((preset) => (
                    <button
                      key={preset.key}
                      type="button"
                      className={styles.presetButton}
                      onClick={() => {
                        setCustomWidth(String(preset.width));
                        setCustomHeight(String(preset.height));
                      }}
                    >
                      {preset.width}×{preset.height}
                    </button>
                  ))}
                </div>
              </div>

              <Button
                mode="primary"
                icon={<DownloadSimple />}
                onClick={handleCustomSizeDownload}
              >
                Download {customWidth}×{customHeight} {customFormat.toUpperCase()}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className={styles.footer}>
        <p>Powered by</p>
        <Logo className={styles.footerLogo} />
      </footer>
    </div>
  );
};

export default PublicShare;

